import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import extractText from 'flarum/common/utils/extractText';

/**
 * Live "someone is typing" badges on discussion-list rows/cards, mirroring
 * flarum/realtime's PostStream indicator out on the index. Realtime relays
 * `client-typing` events on the per-discussion `private-typing={id}` channel;
 * we subscribe for the rows on screen (capped), track who's active with the
 * same 6-second expiry the core indicator uses, and pulse a badge on the row.
 *
 * Everything no-ops when flarum/realtime is absent, websockets are disallowed,
 * or the actor lacks canViewWhoTypes on a discussion.
 */

const entries = new Map();
const EXPIRE_MS = 6000;
const MAX_SUBSCRIPTIONS = 30;

/**
 * Tag ids that currently have someone typing in one of the subscribed
 * (on-screen) discussions — consumed by the tags sidebar widget. Tags come
 * off the store's discussion models, so coverage is exactly the loaded list.
 */
export function typingTagIds() {
  const ids = new Set();
  for (const [discussionId, entry] of entries.entries()) {
    if (!entry.active) continue;
    const discussion = app.store.getById('discussions', discussionId);
    const tags = discussion && discussion.tags && discussion.tags();
    (tags || []).forEach(t => {
      if (t && t.id) ids.add(String(t.id()));
    });
  }
  return ids;
}
function available() {
  return 'flarum-realtime' in flarum.extensions && !app.forum.attribute('websocket.disallow_connection') && typeof app.websocket?.subscribe === 'function';
}
function badgeFor(el) {
  let badge = el.querySelector('.Bespoke-typingBadge');
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'Bespoke-typingBadge';
    badge.innerHTML = '<span class="Bespoke-typingBadge-dots"><i></i><i></i><i></i></span><span class="Bespoke-typingBadge-label"></span>';
    el.appendChild(badge);
  }
  return badge;
}
function refresh(id) {
  const entry = entries.get(id);
  if (!entry) return;
  const cutoff = Date.now() - EXPIRE_MS;
  for (const [name, at] of entry.users.entries()) {
    if (at < cutoff) entry.users.delete(name);
  }
  const count = entry.users.size;
  entry.refs.forEach(el => {
    if (!el.isConnected) {
      entry.refs.delete(el);
      return;
    }
    const badge = badgeFor(el);
    badge.classList.toggle('is-typing', count > 0);
    const label = badge.querySelector('.Bespoke-typingBadge-label');
    if (label) {
      label.textContent = count > 0 ? extractText(app.translator.trans('ernestdefoe-bespoke.forum.typing_count', {
        count
      })) : '';
    }
  });
  if (entry.timer) clearTimeout(entry.timer);
  entry.timer = count > 0 ? setTimeout(() => refresh(id), EXPIRE_MS + 250) : null;
  const nowActive = count > 0;
  if (nowActive !== entry.active) {
    entry.active = nowActive;
    m.redraw();
  }
}
function subscribeCard() {
  if (!available()) return;
  const el = this.element;
  const d = this.attrs && this.attrs.discussion;
  if (!el || !d || el.closest('.DiscussionListPane')) return;
  if (!d.attribute || !d.attribute('canViewWhoTypes')) return;
  const id = String(d.id());
  let entry = entries.get(id);
  if (!entry) {
    if (entries.size >= MAX_SUBSCRIPTIONS) return;
    try {
      const channel = app.websocket.subscribe('private-typing=' + id);
      const handler = data => {
        if (!data) return;
        const name = data.discloseOnline === false ? '·' : String(data.displayName || '·');
        const e = entries.get(id);
        if (!e) return;
        e.users.set(name, typeof data.time === 'number' ? data.time : Date.now());
        refresh(id);
      };
      channel.bind('client-typing', handler);
      entry = {
        channel,
        handler,
        refs: new Set(),
        users: new Map(),
        timer: null,
        active: false
      };
      entries.set(id, entry);
    } catch (e) {
      console.warn('[bespoke]', e);
      return;
    }
  }
  entry.refs.add(el);
}
function unsubscribeCard() {
  const d = this.attrs && this.attrs.discussion;
  if (!d || !d.id) return;
  const id = String(d.id());
  const entry = entries.get(id);
  if (!entry) return;
  entry.refs.delete(this.element);
  if (entry.refs.size > 0) return;
  try {
    entry.channel.unbind('client-typing', entry.handler);
    /*
     * Safe even when the member is navigating INTO this discussion:
     * realtime's PostStream re-subscribes the same channel name on its
     * next view() pass, and Pusher dedupes by name.
     */
    app.websocket.unsubscribe('private-typing=' + id);
  } catch (e) {
    console.warn('[bespoke]', e);
  }
  if (entry.timer) clearTimeout(entry.timer);
  entries.delete(id);
  if (entry.active) m.redraw();
}
export default function typingCards() {
  if (!('flarum-realtime' in flarum.extensions)) return;
  extend(DiscussionListItem.prototype, 'oncreate', subscribeCard);
  extend(DiscussionListItem.prototype, 'onupdate', subscribeCard);
  extend(DiscussionListItem.prototype, 'onremove', unsubscribeCard);
}